/**
 * useViolationQueue (Sprint 6 B5) — buffer + flush violations theo batch.
 *
 * Strategy:
 *   - In-memory queue, FE component push event qua `enqueue()`.
 *   - Flush mỗi 5s qua POST batch endpoint — giảm số request thay vì 1 POST/event.
 *   - Flush ngay khi queue size >= 20 (chống mất event khi tab close trước tick).
 *   - Cleanup tick interval khi unmount.
 *
 * Backend `MAX_VIOLATIONS_PER_BATCH = 100`, em chunk 80 cho an toàn.
 */

import { useCallback, useEffect, useRef } from "react";
import { useLogViolations, type ViolationEvent } from "@/api/proctoring";

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_THRESHOLD = 20;
const MAX_BATCH = 80;

export function useViolationQueue(sessionId: number | undefined) {
  const queueRef = useRef<ViolationEvent[]>([]);
  const mut = useLogViolations(sessionId);

  const flush = useCallback(async () => {
    if (queueRef.current.length === 0) return;
    if (!sessionId) return;
    const batch = queueRef.current.splice(0, MAX_BATCH);
    try {
      await mut.mutateAsync(batch);
    } catch {
      // Re-enqueue lại đầu queue để retry tick sau
      queueRef.current.unshift(...batch);
    }
  }, [sessionId, mut]);

  const enqueue = useCallback(
    (event: Omit<ViolationEvent, "occurred_at"> & { occurred_at?: string }) => {
      queueRef.current.push({
        ...event,
        occurred_at: event.occurred_at ?? new Date().toISOString(),
      });
      if (queueRef.current.length >= FLUSH_THRESHOLD) {
        void flush();
      }
    },
    [flush],
  );

  useEffect(() => {
    if (!sessionId) return;
    const id = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
    return () => {
      clearInterval(id);
      void flush(); // best-effort cuối cùng
    };
  }, [sessionId, flush]);

  return { enqueue, flush };
}
